/**
 * Internal Observability API Service
 * 
 * Provides methods for:
 * - Version info and release notes
 * - Feedback submission
 * - Context capture for bug reports
 */

import api from './api';

const observabilityAPI = {
  // ==========================================================================
  // Version & Release Notes
  // ==========================================================================
  
  /**
   * Get current version info and internal user status
   * @returns {Promise<Object>} Version info with is_internal_user flag
   */
  async getVersionInfo() {
    const response = await api.get('/internal/version/');
    return response.data;
  },
  
  /**
   * Mark current version as seen (hide What's New indicator)
   * @returns {Promise<Object>} Confirmation with marked version
   */
  async markVersionSeen() {
    const response = await api.post('/internal/version/seen/');
    return response.data;
  },
  
  /**
   * Get release notes for internal users
   * @param {string} sinceVersion - Optional: only get notes newer than this version
   * @returns {Promise<Object>} Release notes array
   */
  async getReleaseNotes(sinceVersion = null) {
    const params = sinceVersion ? { since_version: sinceVersion } : {};
    const response = await api.get('/internal/release-notes/', { params });
    return response.data;
  },
  
  // ==========================================================================
  // Feedback Submission
  // ==========================================================================
  
  /**
   * Submit internal feedback (bug, feature request, etc.)
   * @param {Object} feedback - Feedback data
   * @param {string} feedback.feedback_type - 'bug', 'feature', 'feedback', 'question'
   * @param {string} feedback.title - Short summary
   * @param {string} feedback.description - Detailed description
   * @param {string} feedback.priority - 'low', 'medium', 'high', 'critical'
   * @param {string} [feedback.steps_to_reproduce] - For bugs
   * @param {string} [feedback.expected_behavior] - For bugs
   * @param {string} [feedback.actual_behavior] - For bugs
   * @returns {Promise<Object>} Created feedback object
   */
  async submitFeedback(feedback) {
    // Auto-capture context
    const context = this.captureContext();
    
    const response = await api.post('/internal/feedback/', {
      ...feedback,
      ...context,
    });
    return response.data;
  },
  
  /**
   * Get list of feedback items (internal/admin only)
   * @param {Object} filters - Optional filters
   * @param {string} filters.application - 'adi', 'lms', 'couples', 'platform'
   * @param {string} filters.status - 'new', 'acknowledged', 'in_progress', 'resolved', 'wont_fix'
   * @param {string} filters.type - 'bug', 'feature', 'feedback', 'question'
   * @returns {Promise<Array>} List of feedback items
   */
  async getFeedbackList(filters = {}) {
    const response = await api.get('/internal/feedback/', { params: filters });
    return response.data;
  },
  
  /**
   * Get single feedback item with comments
   * @param {number} id - Feedback ID
   * @returns {Promise<Object>} Full feedback object
   */
  async getFeedbackDetail(id) {
    const response = await api.get(`/internal/feedback/${id}/`);
    return response.data;
  },
  
  /**
   * Add comment to feedback
   * @param {number} feedbackId - Feedback ID
   * @param {string} comment - Comment text
   * @param {string} [newStatus] - Optional status change
   * @returns {Promise<Object>} Created comment
   */
  async addFeedbackComment(feedbackId, comment, newStatus = null) {
    const data = { comment };
    if (newStatus) data.new_status = newStatus;
    
    const response = await api.post(`/internal/feedback/${feedbackId}/add_comment/`, data);
    return response.data;
  },
  
  /**
   * Resolve feedback
   * @param {number} feedbackId - Feedback ID
   * @param {string} [notes] - Resolution notes
   * @returns {Promise<Object>} Updated feedback
   */
  async resolveFeedback(feedbackId, notes = '') {
    const response = await api.post(`/internal/feedback/${feedbackId}/resolve/`, {
      resolution_notes: notes
    });
    return response.data;
  },
  
  /**
   * Get feedback statistics (internal/admin only)
   * @returns {Promise<Object>} Stats by status, application, type
   */
  async getFeedbackStats() {
    const response = await api.get('/internal/feedback/stats/');
    return response.data;
  },
  
  // ==========================================================================
  // Context Capture
  // ==========================================================================
  
  /**
   * Capture current page context for feedback submission.
   * Includes URL, route, browser info, screen size, and recent errors.
   * @returns {Object} Context data
   */
  captureContext() {
    const context = {
      page_url: window.location.href,
      page_route: this._getCurrentRouteName(),
      browser_info: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      console_errors: this._getRecentConsoleErrors(),
      network_failures: this._getRecentNetworkFailures(),
    };
    
    return context;
  },
  
  /**
   * Get current Vue route name if available
   * @private
   */
  _getCurrentRouteName() {
    // Try to access Vue Router if available
    if (window.__VUE_APP__ && window.__VUE_APP__.$route) {
      return window.__VUE_APP__.$route.name || '';
    }
    // Fallback: parse from URL
    const path = window.location.pathname;
    return path.split('/').filter(Boolean).join('-') || 'home';
  },
  
  /**
   * Get recent console errors (if captured)
   * @private
   */
  _getRecentConsoleErrors() {
    // Check if error capture is enabled
    if (window.__CAPTURED_ERRORS__) {
      return window.__CAPTURED_ERRORS__.slice(-5); // Last 5 errors
    }
    return [];
  },
  
  /**
   * Get recent network failures (if captured)
   * @private
   */
  _getRecentNetworkFailures() {
    // Check if network failure capture is enabled
    if (window.__NETWORK_FAILURES__) {
      return window.__NETWORK_FAILURES__.slice(-5); // Last 5 failures
    }
    return [];
  },
  
  // ==========================================================================
  // Error Capture Setup (call once at app init)
  // ==========================================================================
  
  /**
   * Initialize error capture for better bug reports.
   * Call this once when the app initializes.
   */
  initErrorCapture() {
    // Capture console errors
    window.__CAPTURED_ERRORS__ = [];
    const originalError = console.error;
    console.error = function(...args) {
      window.__CAPTURED_ERRORS__.push({
        timestamp: new Date().toISOString(),
        message: args.map(a => String(a)).join(' '),
      });
      // Keep only last 10
      if (window.__CAPTURED_ERRORS__.length > 10) {
        window.__CAPTURED_ERRORS__.shift();
      }
      originalError.apply(console, args);
    };
    
    // Capture network failures
    window.__NETWORK_FAILURES__ = [];
    
    // Intercept fetch failures
    const originalFetch = window.fetch;
    window.fetch = async function(...args) {
      try {
        const response = await originalFetch.apply(this, args);
        if (!response.ok) {
          window.__NETWORK_FAILURES__.push({
            timestamp: new Date().toISOString(),
            url: args[0],
            status: response.status,
            statusText: response.statusText,
          });
          if (window.__NETWORK_FAILURES__.length > 10) {
            window.__NETWORK_FAILURES__.shift();
          }
        }
        return response;
      } catch (error) {
        window.__NETWORK_FAILURES__.push({
          timestamp: new Date().toISOString(),
          url: args[0],
          error: error.message,
        });
        if (window.__NETWORK_FAILURES__.length > 10) {
          window.__NETWORK_FAILURES__.shift();
        }
        throw error;
      }
    };
  },
};

export default observabilityAPI;
